import React, { useState, useEffect } from 'react'
import { X, Plus, Minus, Save, ChefHat, Clock, Users, AlertCircle } from 'lucide-react'
import { apiClient } from '@/utils/api'

interface RecipeEditorProps {
  recipeId?: string
  onSave: () => void
  onCancel: () => void
}

interface IngredientGroup {
  name: string
  ingredients: {
    ingredient_name: string
    quantity?: number
    unit?: string
    preparation_notes?: string
    is_optional: boolean
  }[]
}

export default function RecipeEditor({ recipeId, onSave, onCancel }: RecipeEditorProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Recipe fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [photoUrl, setPhotoUrl] = useState('')
  const [mealType, setMealType] = useState('dinner')
  const [prepTime, setPrepTime] = useState(15)
  const [cookTime, setCookTime] = useState(30)
  const [servings, setServings] = useState(4)
  const [difficulty, setDifficulty] = useState('medium')
  const [tags, setTags] = useState<string[]>([])
  const [currentTag, setCurrentTag] = useState('')
  
  // Ingredients
  const [ingredientGroups, setIngredientGroups] = useState<IngredientGroup[]>([
    { name: 'Main', ingredients: [] }
  ])
  
  // Instructions
  const [instructions, setInstructions] = useState<string[]>([''])
  
  // Nutrition
  const [nutrition, setNutrition] = useState({
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fat_g: 0
  })

  useEffect(() => {
    if (recipeId) {
      loadRecipe()
    }
  }, [recipeId])

  const loadRecipe = async () => {
    setLoading(true)
    try {
      const recipe = await apiClient.getRecipe(recipeId!)
      setName(recipe.name)
      setDescription(recipe.description || '')
      setPhotoUrl(recipe.photo_url || '')
      setMealType(recipe.meal_type || 'dinner')
      setPrepTime(recipe.prep_time_minutes || 15)
      setCookTime(recipe.cook_time_minutes || 30)
      setServings(recipe.servings || 4)
      setDifficulty(recipe.difficulty || 'medium')
      setTags(recipe.tags || [])
      
      // Group ingredients
      const groups: Record<string, any[]> = {}
      recipe.ingredients?.forEach((ing: any) => {
        const group = ing.ingredient_group || 'Main'
        if (!groups[group]) groups[group] = []
        groups[group].push(ing)
      })
      
      setIngredientGroups(
        Object.entries(groups).map(([name, ingredients]) => ({
          name,
          ingredients
        }))
      )
      
      // Set instructions
      setInstructions(
        recipe.instructions?.map((inst: any) => inst.instruction) || ['']
      )
      
      // Set nutrition
      if (recipe.nutrition) {
        setNutrition(recipe.nutrition)
      }
    } catch (err) {
      setError('Failed to load recipe')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Recipe name is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const recipeData = {
        name,
        description,
        photo_url: photoUrl || undefined,
        meal_type: mealType,
        prep_time_minutes: prepTime,
        cook_time_minutes: cookTime,
        total_time_minutes: prepTime + cookTime,
        servings,
        difficulty,
        tags,
        source: 'manual',
        source_url: undefined
      }

      let savedRecipeId = recipeId

      if (recipeId) {
        await apiClient.updateRecipe(recipeId, recipeData)
      } else {
        const result = await apiClient.createRecipe(recipeData)
        savedRecipeId = result.id
      }

      // Save ingredients
      const allIngredients = ingredientGroups.flatMap(group =>
        group.ingredients.map(ing => ({
          ...ing,
          ingredient_group: group.name !== 'Main' ? group.name : undefined
        }))
      )

      for (const ingredient of allIngredients) {
        if (ingredient.ingredient_name.trim()) {
          await apiClient.addRecipeIngredient(savedRecipeId!, ingredient)
        }
      }

      // Save instructions
      for (let i = 0; i < instructions.length; i++) {
        if (instructions[i].trim()) {
          await apiClient.addRecipeInstruction(savedRecipeId!, {
            step_number: i + 1,
            instruction: instructions[i],
            time_minutes: undefined
          })
        }
      }

      // Save nutrition if provided
      if (nutrition.calories > 0) {
        await apiClient.updateRecipeNutrition(savedRecipeId!, nutrition)
      }

      onSave()
    } catch (err: any) {
      setError(err.message || 'Failed to save recipe')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const addIngredientToGroup = (groupIndex: number) => {
    const newGroups = [...ingredientGroups]
    newGroups[groupIndex].ingredients.push({
      ingredient_name: '',
      quantity: undefined,
      unit: '',
      preparation_notes: '',
      is_optional: false
    })
    setIngredientGroups(newGroups)
  }

  const updateIngredient = (groupIndex: number, ingredientIndex: number, field: string, value: any) => {
    const newGroups = [...ingredientGroups]
    newGroups[groupIndex].ingredients[ingredientIndex] = {
      ...newGroups[groupIndex].ingredients[ingredientIndex],
      [field]: value
    }
    setIngredientGroups(newGroups)
  }

  const removeIngredient = (groupIndex: number, ingredientIndex: number) => {
    const newGroups = [...ingredientGroups]
    newGroups[groupIndex].ingredients.splice(ingredientIndex, 1)
    setIngredientGroups(newGroups)
  }

  const addIngredientGroup = () => {
    setIngredientGroups([...ingredientGroups, { name: '', ingredients: [] }])
  }

  const addInstruction = () => {
    setInstructions([...instructions, ''])
  }

  const updateInstruction = (index: number, value: string) => {
    const newInstructions = [...instructions]
    newInstructions[index] = value
    setInstructions(newInstructions)
  }

  const removeInstruction = (index: number) => {
    const newInstructions = [...instructions]
    newInstructions.splice(index, 1)
    setInstructions(newInstructions)
  }

  const addTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()])
      setCurrentTag('')
    }
  }

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ChefHat className="w-6 h-6" />
            {recipeId ? 'Edit Recipe' : 'Create Recipe'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Recipe Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="e.g., Spaghetti Carbonara"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
              placeholder="Brief description of the dish..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Photo URL</label>
            <input
              type="url"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Meal Type</label>
              <select
                value={mealType}
                onChange={(e) => setMealType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
                <option value="dessert">Dessert</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Difficulty</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Prep Time (min)</label>
              <input
                type="number"
                value={prepTime}
                onChange={(e) => setPrepTime(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cook Time (min)</label>
              <input
                type="number"
                value={cookTime}
                onChange={(e) => setCookTime(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Servings</label>
            <input
              type="number"
              value={servings}
              onChange={(e) => setServings(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border rounded-lg"
              min="1"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Tags</label>
          <div className="flex gap-2 mb-2 flex-wrap">
            {tags.map(tag => (
              <span
                key={tag}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={currentTag}
              onChange={(e) => setCurrentTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1 px-3 py-2 border rounded-lg"
              placeholder="Add a tag..."
            />
            <button
              onClick={addTag}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Add Tag
            </button>
          </div>
        </div>

        {/* Ingredients */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Ingredients</h3>
          {ingredientGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-4">
              {ingredientGroups.length > 1 && (
                <input
                  type="text"
                  value={group.name}
                  onChange={(e) => {
                    const newGroups = [...ingredientGroups]
                    newGroups[groupIndex].name = e.target.value
                    setIngredientGroups(newGroups)
                  }}
                  className="mb-2 px-3 py-1 border rounded text-sm font-medium"
                  placeholder="Group name (e.g., Sauce)"
                />
              )}
              
              {group.ingredients.map((ingredient, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="number"
                    value={ingredient.quantity || ''}
                    onChange={(e) => updateIngredient(groupIndex, index, 'quantity', parseFloat(e.target.value) || undefined)}
                    className="w-20 px-2 py-1 border rounded"
                    placeholder="Qty"
                    step="0.1"
                  />
                  <input
                    type="text"
                    value={ingredient.unit || ''}
                    onChange={(e) => updateIngredient(groupIndex, index, 'unit', e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    placeholder="Unit"
                  />
                  <input
                    type="text"
                    value={ingredient.ingredient_name}
                    onChange={(e) => updateIngredient(groupIndex, index, 'ingredient_name', e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                    placeholder="Ingredient name"
                  />
                  <input
                    type="text"
                    value={ingredient.preparation_notes || ''}
                    onChange={(e) => updateIngredient(groupIndex, index, 'preparation_notes', e.target.value)}
                    className="w-32 px-2 py-1 border rounded"
                    placeholder="Notes"
                  />
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={ingredient.is_optional}
                      onChange={(e) => updateIngredient(groupIndex, index, 'is_optional', e.target.checked)}
                    />
                    <span className="text-sm">Optional</span>
                  </label>
                  <button
                    onClick={() => removeIngredient(groupIndex, index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => addIngredientToGroup(groupIndex)}
                className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Ingredient
              </button>
            </div>
          ))}
          
          <button
            onClick={addIngredientGroup}
            className="mt-2 text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add Ingredient Group
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Instructions</h3>
          {instructions.map((instruction, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold">
                {index + 1}
              </span>
              <textarea
                value={instruction}
                onChange={(e) => updateInstruction(index, e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Describe this step..."
              />
              <button
                onClick={() => removeInstruction(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <button
            onClick={addInstruction}
            className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add Step
          </button>
        </div>

        {/* Nutrition (Optional) */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Nutrition Information (Optional)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Calories</label>
              <input
                type="number"
                value={nutrition.calories || ''}
                onChange={(e) => setNutrition({...nutrition, calories: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Protein (g)</label>
              <input
                type="number"
                value={nutrition.protein_g || ''}
                onChange={(e) => setNutrition({...nutrition, protein_g: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Carbs (g)</label>
              <input
                type="number"
                value={nutrition.carbs_g || ''}
                onChange={(e) => setNutrition({...nutrition, carbs_g: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fat (g)</label>
              <input
                type="number"
                value={nutrition.fat_g || ''}
                onChange={(e) => setNutrition({...nutrition, fat_g: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Recipe'}
          </button>
        </div>
      </div>
    </div>
  )
}